import React, { useState } from 'react';
import { X, Trash2, Save, Clock, User, Calendar } from 'lucide-react';

const STATUS_OPTIONS = ['pending', 'in_progress', 'completed', 'cancelled'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

export default function TaskDetailModal({ task, onClose, onUpdate, onUpdateStatus, onDelete, theme }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [priority, setPriority] = useState(task.priority);
  const [category, setCategory] = useState(task.category || '');
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.slice(0, 16) : '');

  const handleSave = () => {
    onUpdate(task.id, { title, description, priority, category, due_date: dueDate || null });
    setEditing(false);
  };

  const priorityColor = {
    urgent: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-orange-500', low: 'bg-green-500'
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`w-full max-w-lg rounded-xl shadow-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} max-h-[80vh] flex flex-col`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${priorityColor[task.priority]}`} />
            <h2 className="font-semibold">{editing ? 'Edit Task' : 'Task Details'}</h2>
          </div>
          <div className="flex items-center gap-2">
            {task.is_owner && (
              <button
                onClick={() => { onDelete(task.id); onClose(); }}
                className="p-1.5 rounded text-red-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Delete task"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {editing ? (
            <>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 rounded-lg border text-sm outline-none resize-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              />
              <div className="flex gap-3">
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
                >
                  {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Category"
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
                />
              </div>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              />
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium">{task.title}</h3>
              {task.description && (
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {task.description}
                </p>
              )}

              {/* Meta Info */}
              <div className={`space-y-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span>Assigned to: {task.assigned_to?.first_name || task.assigned_to?.username}</span>
                </div>
                {task.assigned_by && (
                  <div className="flex items-center gap-2">
                    <User size={14} />
                    <span>Assigned by: {task.assigned_by.username}</span>
                  </div>
                )}
                {task.due_date && (
                  <div className={`flex items-center gap-2 ${task.overdue ? 'text-red-500' : ''}`}>
                    <Calendar size={14} />
                    <span>{new Date(task.due_date).toLocaleString()}</span>
                    {task.overdue && <span className="text-xs font-medium">(overdue)</span>}
                  </div>
                )}
                {task.source_type && task.source_type !== 'manual' && (
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span>Source: {task.source_label || task.source_type.replace('_', ' ')}</span>
                  </div>
                )}
              </div>

              {/* Status buttons */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">Status</label>
                <div className="flex gap-2">
                  {STATUS_OPTIONS.map(s => (
                    <button
                      key={s}
                      onClick={() => onUpdateStatus(task.id, s)}
                      className={`flex-1 py-1.5 text-xs rounded-lg border transition-colors ${
                        task.status === s
                          ? 'bg-blue-600 text-white border-transparent'
                          : `${theme === 'dark' ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`
                      }`}
                    >
                      {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-2 px-5 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} className={`px-4 py-2 text-sm rounded-lg ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                Cancel
              </button>
              <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                <Save size={14} /> Save
              </button>
            </>
          ) : (
            task.is_owner && (
              <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                Edit
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
